import { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';

interface SelfieCameraProps {
  isOpen: boolean;
  instruction?: string;
  onCapture: (blob: Blob) => void;
  onClose: () => void;
  onError?: (msg: string) => void;
}

/**
 * Stops every track on a MediaStream — mirrors the pattern in QRScanner.tsx.
 */
function stopMediaStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  stream.getTracks().forEach(track => {
    track.stop();
    track.enabled = false;
  });
}

export default function SelfieCamera({ isOpen, instruction, onCapture, onClose, onError }: SelfieCameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      setReady(false);
      setPermissionDenied(false);
      setCapturing(false);
      return;
    }

    let cancelled = false;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 720 } },
          audio: false
        });

        if (cancelled) {
          stopMediaStream(stream);
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (!cancelled) setReady(true);
          };
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionDenied(true);
          onError?.('Camera permission denied. Please allow camera access and try again.');
        } else {
          onError?.('Could not access camera. Please try again.');
        }
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      stopMediaStream(streamRef.current);
      streamRef.current = null;
    };
  }, [isOpen]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current || !ready || capturing) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const MAX_WIDTH = 720;
    const scale = Math.min(1, MAX_WIDTH / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Mirror the image horizontally to match the preview (selfie convention)
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    setCapturing(true);
    canvas.toBlob(
      blob => {
        if (blob) {
          // Stop stream immediately after capture — no lingering resources
          stopMediaStream(streamRef.current);
          streamRef.current = null;
          setReady(false);
          onCapture(blob);
        }
        setCapturing(false);
      },
      'image/jpeg',
      0.65
    );
  };

  if (!isOpen) return null;

  return (
    <div className="flex flex-col w-full h-full min-h-0">
      {permissionDenied ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-4 p-6 text-center">
          <Camera size={48} className="text-gray-400" />
          <p className="text-gray-700 font-medium">Camera access denied</p>
          <p className="text-sm text-gray-500">
            Please allow camera access in your browser settings and try again.
          </p>
          <button
            onClick={onClose}
            className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      ) : (
        <div className="relative flex flex-col items-center flex-1 min-h-0 bg-black rounded-xl overflow-hidden">
          {/* Video preview — mirrored so it feels like looking in a mirror */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />

          {/* Instruction overlay */}
          {instruction && ready && (
            <div className="absolute top-4 left-0 right-0 flex justify-center pointer-events-none">
              <span className="bg-black/60 text-white text-sm font-medium px-4 py-2 rounded-full">
                {instruction}
              </span>
            </div>
          )}

          {/* Loading state */}
          {!ready && !permissionDenied && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <div className="flex flex-col items-center gap-3 text-white">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Starting camera…</span>
              </div>
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            aria-label="Close camera"
          >
            <X size={16} />
          </button>

          {/* Capture button */}
          {ready && (
            <div className="absolute bottom-6 left-0 right-0 flex justify-center">
              <button
                onClick={handleCapture}
                disabled={capturing}
                className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50"
                aria-label="Capture selfie"
              >
                <div className="w-10 h-10 rounded-full bg-white border-2 border-gray-400" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hidden canvas used for capture/compression */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
